import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { riskProfileMap } from '../../constants/statuses';
import type { RiskProfile } from '../../types/schemas';

// 'none' excluded per UX decision. `very_low` was dropped in migration 0068:
// every stored row moved to `low`, so continuing to offer it here would just
// re-create the bucket the migration cleared. `unknown` leads the list rather
// than sitting at the severity tail — it is not "less severe than low", it is
// "assessed and could not be placed", which is distinct from leaving the picker
// untouched (that leaves the existing value alone).
// riskProfileMap supports all values, retired ones included, for label display.
export const RISK_PICK_OPTIONS: readonly RiskProfile[] = [
  'unknown',
  'low',
  'medium',
  'high',
  'very_high',
  'critical',
];

export const RISK_UNSET = '__undefined__' as const;
export type RiskPick = RiskProfile | typeof RISK_UNSET;
/** Type guard: the picker state holds either a real `RiskProfile` or the
 *  Radix-Select sentinel for "no selection". */
export const isRiskProfile = (v: RiskPick): v is RiskProfile => v !== RISK_UNSET;

interface Props {
  id?: string;
  value: RiskPick;
  onChange: (next: RiskPick) => void;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * Risk profile dropdown shared by the wrap-up dialogs (video Leave & complete,
 * chat End, workspace Mark complete). The caller owns the "required when the
 * client has no risk yet" rule: this only renders the choices.
 */
export function RiskProfileSelect({
  id,
  value,
  onChange,
  disabled,
  placeholder = 'Leave unchanged',
}: Props) {
  return (
    <Select
      value={value}
      disabled={disabled}
      onValueChange={(v) => {
        onChange(v as RiskPick);
      }}
    >
      <SelectTrigger id={id}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {RISK_PICK_OPTIONS.map((r) => (
          <SelectItem key={r} value={r}>
            {riskProfileMap[r].label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Label for the client's current risk, "Undefined" when none is set. */
export function riskLabel(risk: RiskProfile | null | undefined): string {
  return risk !== null && risk !== undefined ? riskProfileMap[risk].label : 'Undefined';
}
